import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { DOCK_RENDER_ZONE } from '@shared/floating-panel/dock-render-zone.token';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { HdContentLanguageService } from '../../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../../bodygraph/hd-gate-info.service';
import { HdGlobalCyclesBraidComponent } from './hd-global-cycles-braid.component';
import { HdGlobalCyclesTableComponent } from './hd-global-cycles-table.component';
import { HdLocksKeysStateService } from './hd-locks-keys-state.service';

export const HD_GLOBAL_CYCLES_TABLE_PANEL_KEY = 'locks-keys-global-cycles-table';

/**
 * Opent (of focust) het zwevende Global Cycles-paneel. Gedeeld door de
 * tabelpreview in het uitlegpaneel en de tabelknop in het Locks & Keys-paneel.
 */
export function openHdGlobalCyclesTablePanel(floatingPanels: FloatingPanelService): void {
  floatingPanels.openOrFocus<HdGlobalCyclesTablePanelComponent, undefined>({
    key: HD_GLOBAL_CYCLES_TABLE_PANEL_KEY,
    component: HdGlobalCyclesTablePanelComponent,
    data: undefined,
    dockTab: { label: 'Global Cycles' },
    dockZone: 'full',
    closable: true,
    startPosition: 'center',
    // Bewust zwevend: groot-maar-niet-fullscreen en versleepbaar naast het
    // rechterpaneel; handmatig docken blijft mogelijk.
    bypassAutoDock: true,
  });
}

/**
 * Zwevend paneel met de volledige Global Cycles-tabel (bijstuurronde 9):
 * groot maar niet fullscreen — ongeveer het inhoudsdeel van de chart-area —
 * en versleepbaar, zodat de tabel naast de teksten in het rechterpaneel kan
 * staan. De rij van de in de app gekozen keys-periode krijgt de dusk-blauwe
 * selectiering. Boven de tabel staat de vlecht van de keys-ster, die de
 * kantwissel per blok in één blik toont; vlecht en kolomkoppen blijven staan
 * terwijl de rijen eronder schuiven. Zwevend heeft het paneel een vaste
 * breedte, gedockt vult het de tab; in beide gevallen scrolt de host.
 */
@Component({
  selector: 'hd-global-cycles-table-panel',
  imports: [HdGlobalCyclesBraidComponent, HdGlobalCyclesTableComponent],
  templateUrl: './hd-global-cycles-table-panel.component.html',
  styleUrl: './hd-global-cycles-table-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.gc-panel-host--floating]': 'floating',
  },
})
export class HdGlobalCyclesTablePanelComponent {
  protected readonly state = inject(HdLocksKeysStateService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly destroyRef = inject(DestroyRef);
  /** Zwevend = niet in een dock-tab gemount (die biedt de dock-zone aan). */
  protected readonly floating = inject(DOCK_RENDER_ZONE, { optional: true }) == null;

  protected readonly hint = computed(() =>
    this.gateInfo.locksKeysTextIn('globalCycles.panelHint', this.contentLang.effectiveLang()),
  );

  /**
   * Hoogte van de kleverige vlecht: daaronder kleven de kolomkoppen van de
   * tabel. Gemeten (ResizeObserver), want de legende onder de vlecht kan
   * met lettertype of taal een regel extra nemen.
   */
  private readonly braidSticky = viewChild<ElementRef<HTMLElement>>('braidSticky');
  protected readonly braidHeight = signal(0);

  constructor() {
    afterNextRender(() => {
      const braid = this.braidSticky()?.nativeElement;
      if (braid == null) {
        return;
      }
      const measure = () => this.braidHeight.set(braid.offsetHeight);
      measure();
      const observer = new ResizeObserver(() => measure());
      observer.observe(braid);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });
  }
}
